import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getExternalCosts, createExternalCost, updateExternalCost, deleteExternalCost } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  DollarSign, 
  Plus, 
  Edit2, 
  Trash2, 
  Check,
  X,
  AlertCircle
} from 'lucide-react';

const ExternalCostsPage = () => {
  const { user } = useAuth();
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    start_month: '',
    end_month: '',
    is_active: true
  });
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const isAdmin = user?.is_admin;

  const fetchCosts = useCallback(async () => {
    try {
      const res = await getExternalCosts();
      setCosts(res.data);
    } catch (err) {
      console.error('Error fetching external costs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchCosts();
    } else {
      setLoading(false);
    }
  }, [isAdmin, fetchCosts]);

  const handleOpenDialog = (cost = null) => {
    setError('');
    if (cost) {
      setEditingCost(cost);
      setFormData({
        name: cost.name,
        amount: cost.amount.toString(),
        start_month: cost.start_month,
        end_month: cost.end_month,
        is_active: cost.is_active
      });
    } else {
      setEditingCost(null);
      const today = new Date();
      const currentMonth = today.toISOString().slice(0, 7);
      const nextYear = new Date(today.getFullYear() + 1, 11, 31).toISOString().slice(0, 7);
      setFormData({
        name: '',
        amount: '',
        start_month: currentMonth,
        end_month: nextYear,
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Název je povinný');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) < 0) {
      setError('Částka musí být nezáporné číslo');
      return;
    }
    if (!formData.start_month || !formData.end_month) {
      setError('Období je povinné');
      return;
    }
    if (formData.start_month > formData.end_month) {
      setError('Začátek období musí být před koncem');
      return;
    }

    try {
      const data = {
        name: formData.name.trim(),
        amount: parseFloat(formData.amount),
        start_month: formData.start_month,
        end_month: formData.end_month,
        is_active: formData.is_active
      };

      if (editingCost) {
        await updateExternalCost(editingCost.id, data);
      } else {
        await createExternalCost(data);
      }

      setDialogOpen(false);
      fetchCosts();
    } catch (err) {
      setError(err.response?.data?.detail || 'Chyba při ukládání');
    }
  };

  const handleDelete = async (costId) => {
    try {
      await deleteExternalCost(costId);
      setDeleteConfirm(null);
      fetchCosts();
    } catch (err) {
      setError(err.response?.data?.detail || 'Chyba při mazání');
    }
  };

  const handleToggleActive = async (cost) => {
    try {
      await updateExternalCost(cost.id, { is_active: !cost.is_active });
      fetchCosts();
    } catch (err) {
      console.error('Error toggling active:', err);
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="font-oswald text-xl uppercase">Přístup odepřen</p>
          <p className="font-serif text-gray-600 mt-2">Tato stránka je pouze pro administrátory.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="font-oswald text-xl uppercase tracking-wide">Načítání...</div>
      </div>
    );
  }

  const activeCosts = costs.filter(c => c.is_active);
  const inactiveCosts = costs.filter(c => !c.is_active);
  const totalMonthly = activeCosts.reduce((sum, c) => sum + c.amount, 0);

  return (
    <div className="space-y-8" data-testid="external-costs-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-oswald text-4xl font-bold uppercase tracking-tight">
            Externí náklady
          </h1>
          <p className="font-serif text-gray-600 mt-1">
            Správa opakujících se nákladů (SaaS, licence, pronájmy)
          </p>
        </div>
        <button
          onClick={() => handleOpenDialog()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#F4E600] text-black font-oswald uppercase text-sm tracking-wide border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] transition-all"
          data-testid="add-cost-btn"
        >
          <Plus size={16} />
          Přidat náklad
        </button>
      </div>

      {/* Summary Box */}
      <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="font-oswald text-xs uppercase tracking-wide text-gray-500">Aktivních nákladů</div>
            <div className="font-oswald text-3xl font-bold">{activeCosts.length}</div>
          </div>
          <div>
            <div className="font-oswald text-xs uppercase tracking-wide text-gray-500">Měsíční náklady</div>
            <div className="font-oswald text-3xl font-bold text-red-600">{formatCurrency(totalMonthly)}</div>
          </div>
          <div>
            <div className="font-oswald text-xs uppercase tracking-wide text-gray-500">Roční náklady</div>
            <div className="font-oswald text-3xl font-bold">{formatCurrency(totalMonthly * 12)}</div>
          </div>
        </div>
      </div>

      {/* Active Costs */}
      <div className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
        <div className="p-4 border-b-2 border-black bg-[#F4E600]">
          <h2 className="font-oswald text-lg font-bold uppercase tracking-tight flex items-center gap-2">
            <Check size={20} />
            Aktivní náklady ({activeCosts.length})
          </h2>
        </div>
        <div className="divide-y-2 divide-black">
          {activeCosts.length === 0 ? (
            <div className="p-8 text-center">
              <DollarSign size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="font-serif text-gray-500">Žádné aktivní náklady</p>
            </div>
          ) : (
            activeCosts.map((cost) => (
              <div 
                key={cost.id} 
                className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                data-testid={`cost-${cost.id}`}
              >
                <div className="flex-1">
                  <div className="font-oswald text-lg font-bold">{cost.name}</div>
                  <div className="text-sm text-gray-500 font-serif">
                    {cost.start_month} → {cost.end_month}
                  </div>
                </div>
                <div className="text-right mr-4">
                  <div className="font-oswald text-xl font-bold text-red-600">
                    {formatCurrency(cost.amount)}/měs
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleActive(cost)}
                    className="p-2 border-2 border-black hover:bg-gray-100 transition-colors"
                    title="Deaktivovat"
                    data-testid={`toggle-${cost.id}`}
                  >
                    <X size={16} />
                  </button>
                  <button
                    onClick={() => handleOpenDialog(cost)}
                    className="p-2 border-2 border-black hover:bg-gray-100 transition-colors"
                    title="Upravit"
                    data-testid={`edit-${cost.id}`}
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(cost.id)}
                    className="p-2 border-2 border-black hover:bg-red-100 transition-colors text-red-600"
                    title="Smazat"
                    data-testid={`delete-${cost.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Inactive Costs */}
      {inactiveCosts.length > 0 && (
        <div className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] opacity-70">
          <div className="p-4 border-b-2 border-black bg-gray-200">
            <h2 className="font-oswald text-lg font-bold uppercase tracking-tight flex items-center gap-2">
              <X size={20} />
              Neaktivní náklady ({inactiveCosts.length})
            </h2>
          </div>
          <div className="divide-y-2 divide-gray-300">
            {inactiveCosts.map((cost) => (
              <div 
                key={cost.id} 
                className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                data-testid={`cost-inactive-${cost.id}`}
              >
                <div className="flex-1">
                  <div className="font-oswald text-lg font-bold text-gray-500">{cost.name}</div>
                  <div className="text-sm text-gray-400 font-serif">
                    {cost.start_month} → {cost.end_month}
                  </div>
                </div>
                <div className="text-right mr-4">
                  <div className="font-oswald text-xl font-bold text-gray-400">
                    {formatCurrency(cost.amount)}/měs
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleActive(cost)}
                    className="p-2 border-2 border-gray-300 hover:bg-green-100 transition-colors text-green-600"
                    title="Aktivovat"
                  >
                    <Check size={16} />
                  </button>
                  <button
                    onClick={() => handleOpenDialog(cost)}
                    className="p-2 border-2 border-gray-300 hover:bg-gray-100 transition-colors"
                    title="Upravit"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(cost.id)}
                    className="p-2 border-2 border-gray-300 hover:bg-red-100 transition-colors text-red-600"
                    title="Smazat"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <DialogHeader>
            <DialogTitle className="font-oswald text-xl uppercase tracking-tight">
              {editingCost ? 'Upravit náklad' : 'Nový externí náklad'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {error && (
              <div className="p-3 bg-red-50 border-2 border-red-400 text-red-700 text-sm font-serif">
                {error}
              </div>
            )}

            <div>
              <label className="block font-oswald text-sm uppercase tracking-wide mb-1">
                Název *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border-2 border-black font-serif focus:outline-none focus:ring-2 focus:ring-[#F4E600]"
                placeholder="např. G-Suite, Slack, AWS"
                data-testid="cost-name-input"
              />
            </div>

            <div>
              <label className="block font-oswald text-sm uppercase tracking-wide mb-1">
                Měsíční částka (Kč) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="w-full px-3 py-2 border-2 border-black font-serif focus:outline-none focus:ring-2 focus:ring-[#F4E600]"
                placeholder="0"
                data-testid="cost-amount-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-oswald text-sm uppercase tracking-wide mb-1">
                  Od měsíce *
                </label>
                <input
                  type="month"
                  value={formData.start_month}
                  onChange={(e) => setFormData({ ...formData, start_month: e.target.value })}
                  className="w-full px-3 py-2 border-2 border-black font-serif focus:outline-none focus:ring-2 focus:ring-[#F4E600]"
                  data-testid="cost-start-input"
                />
              </div>
              <div>
                <label className="block font-oswald text-sm uppercase tracking-wide mb-1">
                  Do měsíce *
                </label>
                <input
                  type="month"
                  value={formData.end_month}
                  onChange={(e) => setFormData({ ...formData, end_month: e.target.value })}
                  className="w-full px-3 py-2 border-2 border-black font-serif focus:outline-none focus:ring-2 focus:ring-[#F4E600]"
                  data-testid="cost-end-input"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-5 h-5 border-2 border-black"
                data-testid="cost-active-checkbox"
              />
              <label htmlFor="is_active" className="font-serif">
                Aktivní (započítávat do nákladů)
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                className="px-4 py-2 border-2 border-black font-oswald uppercase text-sm tracking-wide hover:bg-gray-100 transition-colors"
              >
                Zrušit
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-[#F4E600] border-2 border-black font-oswald uppercase text-sm tracking-wide shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] transition-all"
                data-testid="cost-submit-btn"
              >
                {editingCost ? 'Uložit' : 'Vytvořit'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <DialogHeader>
            <DialogTitle className="font-oswald text-xl uppercase tracking-tight">
              Smazat náklad?
            </DialogTitle>
          </DialogHeader>
          <p className="font-serif text-gray-600 my-4">
            Opravdu chcete trvale smazat tento externí náklad? Tuto akci nelze vrátit zpět.
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setDeleteConfirm(null)}
              className="px-4 py-2 border-2 border-black font-oswald uppercase text-sm tracking-wide hover:bg-gray-100 transition-colors"
            >
              Zrušit
            </button>
            <button
              onClick={() => handleDelete(deleteConfirm)}
              className="px-4 py-2 bg-red-600 text-white border-2 border-black font-oswald uppercase text-sm tracking-wide hover:bg-red-700 transition-colors"
              data-testid="confirm-delete-btn"
            >
              Smazat
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExternalCostsPage;
